/*
 * Project: bellcloud
 * File: apps/assets/configuratorMockData.ts
 * Author: SWAN
 * Created: 2025-11-03
 * Description: Unified device mock data for Mini and Lite configurators
 */

import type { MiniDeviceConfiguration } from '@packages/types/device/mini/deviceConfiguration';
import type { MiniDeviceInfo } from '@packages/types/device/mini/deviceInfo';
import { MiniSensorType } from '@packages/types/device/mini/sensors';
import { getMiniSensorDefaultValues } from '@packages/types/device/mini/mini_constraints';

import { getLiteSensorDefaultValues } from '@packages/types/device/lite/lite_constraints';

// ============================================================================
// Mini Device Mock Data
// ============================================================================

export const MINI_SKELETON_DEVICE_INFO: MiniDeviceInfo = {
  serial_number: 'MINI',
  manufacture_date: new Date().toISOString(),
  hw_version: 'v0.0',
  fw_version: 'v0.0.0',
  equipped_sensor: 255,
  calibration_due_date: '-',
  bump_due_date: '-',
  last_calibration_days: new Date().toISOString(),
  last_bump_days: new Date().toISOString(),
  activation_date_time: new Date().toISOString(),
  decimal_point_and_unit: {
    decimalPoint: 0,
    unit: 'ppm',
  },
};

export const MINI_SKELETON_CONFIG_VALUES: MiniDeviceConfiguration = {
  stealth_mode: false,
  safe_mode: false,
  latching_alarm: false,
  low_alarm_ack: false,
  confidence_beep: false,
  green_light: false,
  confidence_interval: 30,
  intelliflash_interval: 30,
  datalog_interval: 15,
  worker_name: '',
  location_name: '',
  timestamp: new Date().toISOString(),
  calibration_gas_conc: 100,
  calibration_interval: [0],
  bump_interval: [180],
  low_alarm: 30,
  high_alarm: 100,
  twa_alarm: 30,
  stel_alarm: 200,
  stel_period: 15,
  auto_fresh_air_cal: false,
  set_date_time: false,
  twa_period: 8,
};

// CO 센서를 기본으로 한 기본값들을 생성
const defaultCOValues = getMiniSensorDefaultValues(MiniSensorType.CO);

export const MINI_DEFAULT_CONFIG_VALUES: MiniDeviceConfiguration = {
  stealth_mode: false,
  safe_mode: false,
  latching_alarm: false,
  low_alarm_ack: false,
  confidence_beep: false,
  green_light: false,
  confidence_interval: 30,
  intelliflash_interval: 30,
  datalog_interval: 15,
  worker_name: '',
  location_name: '',
  timestamp: new Date().toISOString(),
  calibration_interval: [0],
  bump_interval: [180],
  stel_period: 15,
  auto_fresh_air_cal: false,
  set_date_time: false,
  twa_period: 8,
  calibration_gas_conc: defaultCOValues.calibration_gas_conc,
  low_alarm: defaultCOValues.low_alarm,
  high_alarm: defaultCOValues.high_alarm,
  twa_alarm: defaultCOValues.twa_alarm,
  stel_alarm: defaultCOValues.stel_alarm,
};

// ============================================================================
// Lite Device Mock Data
// ============================================================================

export const LITE_SKELETON_DEVICE_INFO: Record<string, unknown> = {
  serial_number: 'LITE',
  manufacture_date: new Date().toISOString(),
  hw_version: 'v0.0',
  fw_version: 'v0.0.0',
  sensor_type: [
    { value: 0, label: 'O2' },
    { value: 3, label: 'EX' },
    { value: 2, label: 'H2S' },
    { value: 1, label: 'CO' },
    { value: 4, label: 'CO2' },
  ],
  min_calibration_due_date: '-',
  min_bump_test_due_date: '-',
  calibration_due_date: ['', '', '', '', ''],
  bump_due_date: ['', '', '', '', ''],
  last_calibration_days: ['', '', '', '', ''],
  last_bump_days: ['', '', '', '', ''],
  activation_date_time: new Date().toISOString(),
};

export const LITE_SKELETON_CONFIG_VALUES: Record<string, unknown> = {
  stealth_mode: false,
  safe_mode: false,
  latching_alarm: false,
  low_alarm_ack: false,
  confidence_beep: false,
  green_light: false,
  confidence_interval: 30,
  intelliflash_interval: 30,
  datalog_interval: 15,
  worker_name: '',
  location_name: '',
  language_setting: 0,
  startup_message: '',
  timestamp: new Date().toISOString(),
  set_date_time: 0,
  span_cal_and_bump: false,
  sensor_type: [
    { value: 0, label: 'O2' },
    { value: 3, label: 'EX' },
    { value: 2, label: 'H2S' },
    { value: 1, label: 'CO' },
    { value: 4, label: 'CO2' },
  ],
  gas_conc_unit: [
    { value: 1, label: '%VOL' },
    { value: 2, label: '%LEL' },
    { value: 0, label: 'PPM' },
    { value: 0, label: 'PPM' },
    { value: 0, label: 'PPM' },
  ],
  low_alarm: [19.5, 10, 10, 30, 5000],
  high_alarm: [23.5, 20, 15, 100, 30000],
  twa_alarm: [0, 0, 10, 30, 5000],
  stel_alarm: [0, 0, 15, 200, 30000],
  calibration_gas_conc: [18, 50, 25, 100, 20000],
  calibration_interval: [0, 0, 0, 0, 0],
  bump_interval: [180, 180, 180, 180, 180],
  twa_period: [0, 0, 8, 8, 8],
  stel_period: [0, 0, 15, 15, 15],
  sensor_disabled: [false, false, false, false, false],
  auto_fresh_air_cal: [false, false, false, false, false],
  bump_test_threshold: [40, 40, 40, 40, 40], // O2/O2LF는 40 고정, 기타 센서도 기본값 40

  custom_lel: [1, 1, 1, 1, 1],
  lel_CF_Index: [
    { value: 0, label: 'Reserved' },
    { value: 0, label: 'Reserved' },
    { value: 0, label: 'Reserved' },
    { value: 0, label: 'Reserved' },
    { value: 0, label: 'Reserved' },
  ],
  vol_for_50_lel_ch4: [
    { value: 0, label: '2.5%VOL(US)' },
    { value: 0, label: '2.5%VOL(US)' },
    { value: 0, label: '2.5%VOL(US)' },
    { value: 0, label: '2.5%VOL(US)' },
    { value: 0, label: '2.5%VOL(US)' },
  ],
};

/**
 * 장착된 센서 리스트를 기반으로 Lite 기본 설정 생성
 * @param equippedSensors - 디바이스에 장착된 센서 타입 배열
 * @returns 센서에 맞춰진 기본 설정 객체
 */
export const createLiteDefaultConfigForSensors = (
  equippedSensors: Array<{ value: number; label: string }>
): Record<string, unknown> => {
  // 공통 설정
  const config: Record<string, unknown> = {
    stealth_mode: false,
    safe_mode: false,
    latching_alarm: false,
    low_alarm_ack: false,
    confidence_beep: false,
    confidence_interval: 30,
    intelliflash_interval: 30,
    green_light: true,
    datalog_interval: 15,
    worker_name: '',
    location_name: '',
    language_setting: 0,
    startup_message: '',
    timestamp: new Date().toISOString(),
    set_date_time: 0,
    span_cal_and_bump: false,
    sensor_type: equippedSensors,
  };

  if (equippedSensors.length === 0) {
    return config;
  }

  // 각 센서별로 기본값 가져와서 배열 구성
  config.gas_conc_unit = equippedSensors.map(
    sensor => getLiteSensorDefaultValues(sensor.value).gas_conc_unit
  );
  config.calibration_gas_conc = equippedSensors.map(
    sensor => getLiteSensorDefaultValues(sensor.value).calibration_gas_conc
  );
  config.calibration_interval = equippedSensors.map(
    sensor => getLiteSensorDefaultValues(sensor.value).calibration_interval
  );
  config.bump_interval = equippedSensors.map(
    sensor => getLiteSensorDefaultValues(sensor.value).bump_interval
  );
  config.low_alarm = equippedSensors.map(
    sensor => getLiteSensorDefaultValues(sensor.value).low_alarm
  );
  config.high_alarm = equippedSensors.map(
    sensor => getLiteSensorDefaultValues(sensor.value).high_alarm
  );
  config.twa_alarm = equippedSensors.map(
    sensor => getLiteSensorDefaultValues(sensor.value).twa_alarm
  );
  config.stel_alarm = equippedSensors.map(
    sensor => getLiteSensorDefaultValues(sensor.value).stel_alarm
  );
  config.twa_period = equippedSensors.map(
    sensor => getLiteSensorDefaultValues(sensor.value).twa_period
  );
  config.stel_period = equippedSensors.map(
    sensor => getLiteSensorDefaultValues(sensor.value).stel_period
  );
  config.sensor_disabled = equippedSensors.map(
    sensor => getLiteSensorDefaultValues(sensor.value).sensor_disabled
  );
  config.auto_fresh_air_cal = equippedSensors.map(
    sensor => getLiteSensorDefaultValues(sensor.value).auto_fresh_air_cal
  );
  config.bump_test_threshold = equippedSensors.map(
    sensor => getLiteSensorDefaultValues(sensor.value).bump_test_threshold
  );
  config.custom_lel = equippedSensors.map(
    sensor => getLiteSensorDefaultValues(sensor.value).custom_lel
  );
  config.lel_CF_Index = equippedSensors.map(
    sensor => getLiteSensorDefaultValues(sensor.value).lel_CF_Index
  );
  config.vol_for_50_lel_ch4 = equippedSensors.map(
    sensor => getLiteSensorDefaultValues(sensor.value).vol_for_50_lel_ch4
  );

  return config;
};
